import React, { useContext, useEffect, useMemo, useState } from "react";
import { Form, Button, Badge, Alert } from "react-bootstrap";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import ProjectMemberPicker from "../../components/ProjectMemberPicker";
import SearchableSelect from "../../components/SearchableSelect";
import Can from "../../components/Can";
import QuickCreateProjectModal from "./QuickCreateProjectModal";
import QuickCreateModuleModal from "./QuickCreateModuleModal";
import { toast } from "react-toastify";
import { isTitlelessTaskRole } from "../../utils/taskDisplay";

const PRIORITY_VALUES = ["low", "normal", "high"];
const ATTACHMENT_TYPES = [
  "REQUIREMENT", "SPECIFICATION", "CHANGE_REQUEST", "SCREENSHOT",
  "ERROR_REPORT", "TESTING_EVIDENCE", "APPROVAL_DOCUMENT", "SUPPORTING_DOCUMENT", "OTHER",
];

// Simplified backdated Task Date (Phase 37, simplified) -- local-date
// "YYYY-MM-DD", matching a native <input type="date">'s own value format.
// Never uses toISOString() (UTC-based -- can roll the calendar date back a
// day in a timezone east of UTC); built from local Date getters instead so
// "today" always means the browser's own local today.
function todayDateString() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

const initialForm = {
  task_type_id: "",
  title: "",
  description: "",
  department_id: "",
  project_id: "",
  project_module_id: "",
  project_phase_id: "",
  project_milestone_id: "",
  priority: "",
  assigned_to: "",
  requested_for: "",
  category_id: "",
  sub_category_id: "",
  // Simplified backdated Task Date (Phase 37, simplified) -- defaults to
  // today via its own effect below (like requested_for's own default),
  // never baked into this module-level constant (which would go stale
  // across midnight without a hard refresh). Available to EVERY
  // role/permission that can already reach this form at all -- no
  // create_historical permission, no feature flag.
  task_date: "",
};

const DEV_OR_DA_ROLES = new Set(["developer", "data-analyst"]);

// pages/tasks/TaskCreateForm.jsx
//
// Phase 2A common task creation FORM -- extracted out of CreateTaskPage.jsx
// so the exact same business logic/validation/API call renders in TWO
// places without ANY duplication:
//   - the standalone routes (/​<role>/tasks/create, /​<role>/tasks/
//     raise-complaint), via CreateTaskPage.jsx, unchanged behavior --
//     `embedded` is false/omitted there, so it keeps its own inline
//     "created successfully + attachment upload" screen exactly as before.
//   - a modal popup on ComplaintsListPage.jsx/MyTicketsListPage.jsx, via
//     TaskCreateModal.jsx, with `embedded` true -- `onCreated(createdTask)`
//     fires immediately on success (every time, whether or not there's an
//     attachment step) so the list page's own existing fetch function can
//     refresh the table right away -- never a full page reload, and never
//     gated on the user finishing/skipping attachments. `onSuccess` fires
//     only once the flow is truly done and the modal should close: right
//     away when the task type doesn't allow attachments (unchanged from
//     before this feature), or after an inline success+attachment step
//     (same UI the standalone page already had) once the user clicks
//     Done, when it does.
//
// ALL existing rules are unchanged and untouched by this extraction:
//   - Staff always renders complaint mode (`isStaff` unconditional).
//   - `mode="complaint"` is the ONLY other way to get complaint mode --
//     Developer/Data-Analyst's own `mode` (undefined, i.e. their "Create
//     Ticket"/My Tickets flow) never implicitly becomes complaint mode.
//   - task_type_id auto-resolves to IT_SUPPORT in complaint mode; IT_SUPPORT
//     is filtered OUT of the dropdown for Developer/Data-Analyst's non-
//     complaint mode.
//   - Department/Project/Project Module/Assigned-To/Staff-User fields stay
//     hidden in complaint mode exactly as before.
//   - same POST /tasks/create payload/endpoint, same tasks:create
//     permission gate at the ROUTE level (PermissionRoute) for the
//     standalone pages; the modal's OWN visibility is gated by
//     can('tasks','create') in the list pages, never bypassing a DENY.
//
// Complaint mode never lets the caller choose a technician: the backend
// (services/taskCreationService.js#resolveUnitComplaintTechnician) assigns
// one from the caller's own unit for the selected Category/Subcategory, and
// ignores any assigned_to a Staff/Data-Analyst/Developer sends. The created
// task's assigned_to_name is shown on the success screen.
export default function TaskCreateForm({ mode, embedded = false, onSuccess, onCreated, onCancel, onDirtyChange }) {
  const { user } = useContext(AuthContext);
  const isStaff = user?.role === "staff";
  const isDevOrDA = DEV_OR_DA_ROLES.has(user?.role);
  const isComplaintMode = isStaff || mode === "complaint";
  // Staff / Technician: Category + Subcategory only -- no Title/Description.
  const isTitleless = isTitlelessTaskRole(user?.role);

  const [taskTypes, setTaskTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectModules, setProjectModules] = useState([]);
  const [projectPhases, setProjectPhases] = useState([]);
  const [projectMilestones, setProjectMilestones] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [requestForOptions, setRequestForOptions] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  // Set only when Task Type/Category (the fields actually REQUIRED to
  // render/submit this form) fail to load -- drives a specific, retryable
  // banner instead of a generic toast the user has no way to act on. A
  // Department/Project/eligible-users failure (secondary, non-blocking
  // options) never sets this -- see fetchStaticData below.
  const [optionsLoadError, setOptionsLoadError] = useState(false);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [projectsLoaded, setProjectsLoaded] = useState(false);

  const [createdTask, setCreatedTask] = useState(null);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [attachmentType, setAttachmentType] = useState("SUPPORTING_DOCUMENT");
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [uploadedAttachments, setUploadedAttachments] = useState([]);

  const [tatPreview, setTatPreview] = useState(null);
  const [loadingTatPreview, setLoadingTatPreview] = useState(false);

  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showNewModuleModal, setShowNewModuleModal] = useState(false);

  useEffect(() => {
    fetchStaticData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.id && !form.requested_for) {
      setForm((prev) => (prev.requested_for ? prev : { ...prev, requested_for: String(user.id) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Simplified backdated Task Date (Phase 37, simplified) -- defaults to
  // today, same "seed once, never overwrite a value already there" pattern
  // as requested_for above (computed fresh at mount rather than baked into
  // the module-level initialForm constant, which would go stale across
  // midnight without a hard refresh).
  useEffect(() => {
    setForm((prev) => (prev.task_date ? prev : { ...prev, task_date: todayDateString() }));
  }, []);

  useEffect(() => {
    if (!form.task_type_id) {
      setTatPreview(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        setLoadingTatPreview(true);
        const { data } = await api.post("/tat-rules/preview", {
          task_type_id: form.task_type_id,
          priority: form.priority || undefined,
          department_id: form.department_id || undefined,
          project_id: form.project_id || undefined,
          project_module_id: form.project_module_id || undefined,
          category_id: form.category_id || undefined,
          sub_category_id: form.sub_category_id || undefined,
        });
        setTatPreview(data?.data || null);
      } catch (err) {
        console.error("TAT preview error:", err);
        setTatPreview(null);
      } finally {
        setLoadingTatPreview(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [form.task_type_id, form.priority, form.department_id, form.project_id, form.project_module_id, form.category_id, form.sub_category_id]);

  // Reports "has the user typed/selected anything yet" up to an embedding
  // modal, so it can avoid silently discarding in-progress input on an
  // accidental backdrop click. task_type_id/requested_for/task_date are
  // excluded -- each gets auto-populated with a default (IT_SUPPORT / the
  // caller's own id / today) as soon as static data loads or on mount,
  // which is not a user edit and must not make an otherwise-untouched form
  // look "dirty".
  const DIRTY_TRACKED_FIELDS = useMemo(
    () => Object.keys(initialForm).filter((key) => key !== "task_type_id" && key !== "requested_for" && key !== "task_date"),
    []
  );
  useEffect(() => {
    if (!onDirtyChange) return;
    const dirty = DIRTY_TRACKED_FIELDS.some((key) => form[key] && form[key] !== initialForm[key]);
    onDirtyChange(dirty);
  }, [form, onDirtyChange, DIRTY_TRACKED_FIELDS]);

  const fetchProjects = async () => {
    try {
      // Backend-scoped by the caller's effective Project Management VIEW
      // access scope (services/dataScopeService.js) -- never widened or
      // re-filtered on the client. An empty result is a real "no accessible
      // projects" answer, not a loading state, and must not fall back to
      // showing every project.
      const res = await api.get("/projects", { params: { is_active: 1, pageSize: 200 } });
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setProjects(list);
      return list;
    } catch (err) {
      console.error("Fetch projects error:", err);
      setProjects([]);
      return [];
    } finally {
      setProjectsLoaded(true);
    }
  };

  // Task Type + Category/Subcategory are the fields actually REQUIRED to
  // render and submit this form (Category/Subcategory only render once a
  // Task Type resolves at all -- see selectedTaskType below) -- fetched and
  // error-handled independently of every other, non-blocking option below,
  // so a Department/Project/eligible-users permission gap can never blank
  // out Category/Subcategory, and vice versa. A real failure here surfaces
  // a specific, retryable banner (optionsLoadError) instead of a vague
  // toast the user has no way to act on.
  const fetchCoreOptions = async () => {
    try {
      setOptionsLoadError(false);
      const typeRes = await api.get("/task-types", { params: { is_active: 1, pageSize: 200 } });
      setTaskTypes(Array.isArray(typeRes.data?.data) ? typeRes.data.data : []);

      if (user?.unit_id) {
        const catRes = await api.get(`/categories/by-unit/${user.unit_id}`);
        setCategories(Array.isArray(catRes.data) ? catRes.data : []);
      }
    } catch (err) {
      console.error("Fetch task type/category options error:", err);
      setOptionsLoadError(true);
      toast.error("Unable to load complaint categories. Please retry.");
    }
  };

  const fetchStaticData = async () => {
    await fetchCoreOptions();

    try {
      const requests = [
        api.get("/tasks/eligible-users", { params: { purpose: "requested_for" } }),
      ];
      // Department/Project/Module and Assigned To are never rendered in
      // complaint mode (see the `{!isComplaintMode && (...)}` guards below;
      // the technician is assigned server-side) -- so they're never fetched
      // for it either, rather than requiring departments:view/tasks:assign
      // permissions this flow doesn't need.
      if (!isComplaintMode) {
        requests.push(
          api.get("/tasks/eligible-users", { params: { purpose: "assigned_to" } }),
          api.get("/departments", { params: { is_active: 1, pageSize: 200 } })
        );
      }

      const [requestForRes, assignableRes, deptRes] = await Promise.all(requests);
      setRequestForOptions(Array.isArray(requestForRes.data?.data) ? requestForRes.data.data : []);
      if (!isComplaintMode) {
        setAssignableUsers(Array.isArray(assignableRes?.data?.data) ? assignableRes.data.data : []);
        setDepartments(Array.isArray(deptRes?.data?.data) ? deptRes.data.data : []);
      }
    } catch (err) {
      // Non-fatal: Task Type/Category/Subcategory (the actually-required
      // fields, fetched above) are unaffected. Department/Assigned To/
      // Staff-User simply render with fewer options, exactly like today
      // when a caller lacks one of those specific permissions.
      console.error("Fetch secondary create-task options error:", err);
    }

    if (!isComplaintMode) await fetchProjects();
  };

  useEffect(() => {
    if (!isComplaintMode || form.task_type_id || !taskTypes.length) return;
    const itSupport = taskTypes.find((t) => t.code === "IT_SUPPORT");
    if (itSupport) setForm((prev) => ({ ...prev, task_type_id: String(itSupport.id) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplaintMode, taskTypes, form.task_type_id]);

  const selectedTaskType = useMemo(
    () => taskTypes.find((t) => String(t.id) === String(form.task_type_id)) || null,
    [taskTypes, form.task_type_id]
  );
  // Staff / Technician tasks have no title, so Category + Subcategory are
  // always required for them (the backend enforces the same).
  const categoryRequired = isTitleless || !!selectedTaskType?.category_required;
  const subcategoryRequired = isTitleless || !!selectedTaskType?.subcategory_required;

  const visibleTaskTypes = useMemo(
    () => (isDevOrDA && !isComplaintMode ? taskTypes.filter((t) => t.code !== "IT_SUPPORT") : taskTypes),
    [taskTypes, isDevOrDA, isComplaintMode]
  );

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleTaskTypeChange = (taskTypeId) => {
    const nextTaskType =
      taskTypes.find((t) => String(t.id) === String(taskTypeId)) || null;

    setForm((prev) => ({
      ...prev,
      task_type_id: taskTypeId,
      category_id: "",
      sub_category_id: "",

      // Project is selected before Task Type in the UI.
      // Keep it when the selected task type allows projects; otherwise clear it.
      project_id:
        nextTaskType && !nextTaskType.project_allowed ? "" : prev.project_id,
      project_module_id:
        nextTaskType && !nextTaskType.project_module_allowed
          ? ""
          : prev.project_module_id,
      assigned_to:
        nextTaskType && !nextTaskType.project_allowed ? "" : prev.assigned_to,
    }));

    setSubCategories([]);

    if (nextTaskType && !nextTaskType.project_allowed) {
      setProjectModules([]);
      setProjectMembers([]);
    } else if (nextTaskType && !nextTaskType.project_module_allowed) {
      setProjectModules([]);
    }
  };

  // Section 7 (Phase 37 production-readiness review): a previously-selected
  // Assigned Technician must never be left attached once Category changes --
  // their eligibility was resolved against the OLD subcategory (or no
  // subcategory at all), which the new Category invalidates immediately
  // (Subcategory itself is cleared on the very next line too). Only the
  // form field is cleared here; the eligible-technician REFETCH itself
  // happens in the sub_category_id effect below, keyed off Subcategory
  // (never Category directly) -- Category alone never determines
  // eligibility, only the subcategory does.
  const handleCategoryChange = async (categoryId) => {
    handleChange("category_id", categoryId);
    handleChange("sub_category_id", "");
    handleChange("assigned_to", "");
    setSubCategories([]);
    if (!categoryId || !user?.unit_id) return;
    try {
      setLoadingSubCategories(true);
      const res = await api.get(`/subcategories/by-category/${categoryId}/unit/${user.unit_id}`);
      setSubCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch subcategories error:", err);
    } finally {
      setLoadingSubCategories(false);
    }
  };

  // Section 7 (Phase 37 production-readiness review): once a Subcategory is
  // chosen, the Assigned Technician dropdown must show ONLY technicians
  // eligible for it (the existing sub_category_staff mapping) — re-fetches
  // the SAME /tasks/eligible-users endpoint fetchStaticData already used,
  // just narrowed. Clearing the subcategory (or changing category) reverts
  // to the actor's full unfiltered eligible-assignee list, exactly like
  // before this feature. A previously-selected Assigned Technician is
  // ALWAYS cleared here too — their eligibility was resolved against
  // whatever the subcategory USED to be, never the new one, so leaving them
  // selected could silently attach an incompatible technician until Submit
  // (where the backend would reject it, but only then). Backend
  // re-validates this same mapping at creation time regardless
  // (services/taskCreationService.js#validateAssigneeCategoryEligibility) —
  // this is a UX narrowing only, never the enforcement point.
  const subCategorySkipFirstRun = React.useRef(true);
  useEffect(() => {
    // fetchStaticData() already loaded the unfiltered list once on mount --
    // skip that redundant duplicate fetch; only re-fetch on an ACTUAL
    // subcategory change/clear after mount.
    if (subCategorySkipFirstRun.current) {
      subCategorySkipFirstRun.current = false;
      return;
    }
    handleChange("assigned_to", "");
    // Complaint mode has no Assigned To field (auto-assigned server-side).
    if (isComplaintMode) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/tasks/eligible-users", {
          params: {
            purpose: "assigned_to",
            sub_category_id: form.sub_category_id || undefined,
          },
        });
        if (!cancelled) setAssignableUsers(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (err) {
        if (!cancelled) console.error("Fetch category-scoped assignees error:", err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.sub_category_id]);

  const fetchModulesAndMembers = async (projectId) => {
    if (!projectId) {
      setProjectModules([]);
      setProjectMembers([]);
      setProjectPhases([]);
      setProjectMilestones([]);
      return;
    }
    try {
      setLoadingModules(true);
      const [moduleRes, memberRes] = await Promise.all([
        api.get(`/projects/${projectId}/modules`, { params: { is_active: 1, pageSize: 200 } }),
        api.get(`/projects/${projectId}/members`, { params: { pageSize: 200 } }),
      ]);
      setProjectModules(Array.isArray(moduleRes.data?.data) ? moduleRes.data.data : []);
      setProjectMembers(Array.isArray(memberRes.data?.data) ? memberRes.data.data : []);

      // Phase/Milestone dropdowns -- fetched alongside modules/members so a
      // project switch refreshes all four together. Each is fetched with
      // its own catch so a failure here (e.g. project has no process
      // configured yet) never blocks Module/Member loading above.
      const [phaseRes, milestoneRes] = await Promise.all([
        api.get(`/projects/${projectId}/phases`, { params: { pageSize: 200 } }).catch(() => null),
        api.get(`/projects/${projectId}/milestones`).catch(() => null),
      ]);
      setProjectPhases(Array.isArray(phaseRes?.data?.data) ? phaseRes.data.data : []);
      setProjectMilestones(Array.isArray(milestoneRes?.data?.data) ? milestoneRes.data.data : []);
    } catch (err) {
      console.error("Fetch project modules/members error:", err);
    } finally {
      setLoadingModules(false);
    }
  };

  const handleProjectChange = async (projectId) => {
    handleChange("project_id", projectId);
    handleChange("project_module_id", "");
    handleChange("assigned_to", "");
    handleChange("project_phase_id", "");
    handleChange("project_milestone_id", "");
    await fetchModulesAndMembers(projectId);
  };

  // Selecting a phase narrows which milestones are relevant (see
  // milestoneOptions below) -- clear any milestone picked under a
  // different/no phase so the submitted pair never contradicts itself.
  const handlePhaseChange = (phaseId) => {
    handleChange("project_phase_id", phaseId);
    handleChange("project_milestone_id", "");
  };

  // A milestone is offered here if it isn't scoped to any phase (applies to
  // the whole project) or if it belongs to the currently selected phase --
  // same filter rule as the locked backend contract describes.
  const milestoneOptions = useMemo(
    () =>
      projectMilestones.filter(
        (m) => !m.project_phase_id || String(m.project_phase_id) === String(form.project_phase_id)
      ),
    [projectMilestones, form.project_phase_id]
  );

  const selectedProject = useMemo(
    () => projects.find((p) => String(p.id) === String(form.project_id)) || null,
    [projects, form.project_id]
  );

  // "+ New Project" -- reuses the existing POST /projects endpoint (same
  // one Project Management uses), gated by <Can moduleKey="projects"
  // actionKey="create"> around the trigger button. On success the project
  // list is refreshed and the new project is auto-selected, which also
  // kicks off the normal project-change flow (module/member fetch).
  const handleProjectCreated = async (project) => {
    setShowNewProjectModal(false);
    if (!project?.id) return;
    await fetchProjects();
    await handleProjectChange(String(project.id));
  };

  // "+ New Module" -- reuses the existing POST /projects/:id/modules
  // endpoint (same one Project Module Management uses), gated by <Can
  // moduleKey="project_modules" actionKey="create">. On success the module
  // list for the currently selected project is refreshed and the new
  // module is auto-selected.
  const handleModuleCreated = async (projectModule) => {
    setShowNewModuleModal(false);
    if (!projectModule?.id || !form.project_id) return;
    await fetchModulesAndMembers(form.project_id);
    handleChange("project_module_id", String(projectModule.id));
  };

  const assigneeOptions = useMemo(() => {
    if (form.project_id && projectMembers.length) {
      return projectMembers.map((pm) => ({
        id: pm.user_id,
        name: pm.user_name,
        employee_code: pm.employee_code,
        designation_name: pm.designation_name,
        department_name: pm.department_name,
      }));
    }
    return form.project_id ? [] : assignableUsers;
  }, [form.project_id, projectMembers, assignableUsers]);

  // Estimated TAT display fallback: `tatPreview` (the full tat_rules
  // hierarchy preview, POST /tat-rules/preview) is the primary source when
  // it resolves something -- it can reflect a department/project/priority
  // rule that legitimately overrides the plain Subcategory value. But when
  // it resolves NOTHING for a chosen Subcategory that genuinely DOES have
  // its own configured tat_value/tat_unit, that must never be shown as "not
  // configured" -- `subCategories` (fetched alongside the dropdown options,
  // services/subcategoryTatService.js's same `sub_categories` source) is
  // looked up directly here, from the full record, not from the
  // SearchableSelect options list (which only keeps {value, label} and
  // drops tat_value/tat_unit entirely). Purely a display fallback --
  // submission never sends tat_value/tat_unit either way; the backend
  // independently resolves TAT from sub_category_id alone.
  const selectedSubCategory = subCategories.find((sc) => String(sc.id) === String(form.sub_category_id));
  const subCategoryHasTat = !!(selectedSubCategory && selectedSubCategory.tat_value != null && selectedSubCategory.tat_unit);

  const handleSubmit = async () => {
    if (!selectedTaskType) {
      toast.warning("Please select a task type");
      return;
    }
    if (!isTitleless && !form.title.trim()) {
      toast.warning("Title is required");
      return;
    }
    if (categoryRequired && !form.category_id) {
      toast.warning("Category is required for this task type");
      return;
    }
    if (subcategoryRequired && !form.sub_category_id) {
      toast.warning("Subcategory is required for this task type");
      return;
    }
    if (selectedTaskType.project_required && !form.project_id) {
      toast.warning("Project is required for this task type");
      return;
    }
    if (selectedTaskType.project_module_required && !form.project_module_id) {
      toast.warning("Project module is required for this task type");
      return;
    }
    // Simplified backdated Task Date (Phase 37, simplified) -- UX-only
    // pre-check; the backend re-validates independently and authoritatively
    // (services/taskCreationService.js#validateTaskDate) regardless of what
    // passes here, including rejecting a crafted future date.
    if (!form.task_date) {
      toast.warning("Task Date is required");
      return;
    }

    const payload = {
      task_type_id: Number(form.task_type_id),
      // Staff / Technician send no title/description (stored as NULL).
      ...(isTitleless ? {} : { title: form.title.trim(), description: form.description.trim() || null }),
      department_id: form.department_id || null,
      project_id: form.project_id || null,
      project_module_id: form.project_module_id || null,
      project_phase_id: form.project_phase_id || null,
      project_milestone_id: form.project_milestone_id || null,
      priority: form.priority || null,
      // Complaint mode: never sent -- the backend assigns the technician.
      assigned_to: isComplaintMode ? null : form.assigned_to || null,
      requested_for: form.requested_for || null,
      category_id: form.category_id || null,
      sub_category_id: form.sub_category_id || null,
      task_date: form.task_date,
      // No tat_value/tat_unit is ever sent -- TAT is resolved purely
      // server-side from category_id/sub_category_id (see
      // services/subcategoryTatService.js). The Estimated TAT section below
      // is read-only; there's no user input to send.
    };

    try {
      setSaving(true);
      const res = await api.post("/tasks/create", payload);
      const task = res.data?.data || null;
      toast.success(isComplaintMode ? "Complaint submitted successfully" : isDevOrDA ? "Ticket created successfully" : "Task created successfully");
      // The task already exists in the DB at this point -- tell the caller
      // right away (list refresh), independent of whether an attachment
      // step follows or the modal is still open.
      onCreated?.(task);
      if (embedded && selectedTaskType?.attachment_allowed) {
        // Reuses the SAME inline success+attachment screen the standalone
        // page has always had (below) -- just also rendered inside the
        // modal now, closing only once the user clicks Done.
        setCreatedTask(task);
      } else if (embedded) {
        // No attachments possible for this task type -- close immediately,
        // exactly as before this feature.
        onSuccess?.(task);
      } else {
        setCreatedTask(task);
        onSuccess?.(task);
      }
    } catch (err) {
      console.error("Create task error:", err);
      toast.error(err.response?.data?.message || (isComplaintMode ? "Failed to submit complaint" : isDevOrDA ? "Failed to create ticket" : "Failed to create task"));
    } finally {
      setSaving(false);
    }
  };

  const handleAttachmentUpload = async () => {
    if (!createdTask?.id || !attachmentFiles.length) return;
    try {
      setUploadingAttachments(true);
      for (const file of attachmentFiles) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("attachment_type", attachmentType);
        const res = await api.post(`/tasks/${createdTask.id}/attachments`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setUploadedAttachments((prev) => [...prev, res.data?.data]);
      }
      toast.success("Attachments uploaded");
      setAttachmentFiles([]);
    } catch (err) {
      console.error("Upload attachment error:", err);
      toast.error(err.response?.data?.message || "Failed to upload one or more attachments");
    } finally {
      setUploadingAttachments(false);
    }
  };

  const resetForNewTask = () => {
    setForm(initialForm);
    setCreatedTask(null);
    setAttachmentFiles([]);
    setUploadedAttachments([]);
    setSubCategories([]);
    setProjectModules([]);
    setProjectMembers([]);
    setProjectPhases([]);
    setProjectMilestones([]);
  };

  // In embedded (modal) mode, `createdTask` is only ever set when the task
  // type allows attachments (see handleSubmit above) -- otherwise the modal
  // already closed via onSuccess before this could render at all. Standalone
  // page behavior (always sets createdTask) is unchanged.
  if (createdTask) {
    return (
      <div className={embedded ? undefined : "unit-card p-4"}>
        <Alert variant="success">
          <div className="fw-semibold mb-2">
            {isComplaintMode ? "Complaint Submitted Successfully" : "Task Created Successfully"}
          </div>
          <div className="small">
            <div>{isComplaintMode ? "Complaint Number" : "Task Number"}: <strong>#{createdTask.id}</strong></div>
            <div>Status: <strong>Pending</strong></div>
            {isComplaintMode && (createdTask.category_name || createdTask.sub_category_name) && (
              <div>Issue: <strong>{[createdTask.category_name, createdTask.sub_category_name].filter(Boolean).join(" / ")}</strong></div>
            )}
            {isComplaintMode && (
              <div>
                Assigned Technician:{" "}
                <strong>{createdTask.assigned_to_name || "Pending assignment"}</strong>
              </div>
            )}
            {createdTask.due_at && (
              <div>Expected TAT: <strong>{new Date(createdTask.due_at).toLocaleString()}</strong></div>
            )}
            {!isComplaintMode && <div>Task Type: <strong>{createdTask.task_type_code}</strong></div>}
          </div>
        </Alert>

        {selectedTaskType?.attachment_allowed && (
          <div className="mb-4">
            <h5>Attachments</h5>
            <p className="text-muted">Upload supporting documents for this task (PDF, images, Office documents, text — up to 10 MB each).</p>

            <div className="row g-3 align-items-end mb-3">
              <div className="col-md-6">
                <Form.Label>Files</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={(e) => setAttachmentFiles(Array.from(e.target.files || []))}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Attachment Type</Form.Label>
                <SearchableSelect
                  options={ATTACHMENT_TYPES.map((t) => ({ value: t, label: t }))}
                  value={attachmentType}
                  onChange={(v) => setAttachmentType(v || attachmentType)}
                  isClearable={false}
                />
              </div>
              <div className="col-md-2">
                <Button
                  className="unit-btn unit-btn-primary w-100"
                  onClick={handleAttachmentUpload}
                  disabled={!attachmentFiles.length || uploadingAttachments}
                >
                  {uploadingAttachments ? "Uploading..." : "Upload"}
                </Button>
              </div>
            </div>

            {uploadedAttachments.length > 0 && (
              <ul className="list-unstyled">
                {uploadedAttachments.map((a) => (
                  <li key={a?.id}>
                    <Badge bg="light" text="dark">{a?.original_name}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {embedded ? (
          <Button className="unit-btn unit-btn-primary" onClick={() => onSuccess?.(createdTask)}>
            Done
          </Button>
        ) : (
          <Button className="unit-btn unit-btn-light" onClick={resetForNewTask}>
            Create Another Task
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={`tcf-form${embedded ? "" : " unit-card p-4"}`}>
      <div className="tcf-section">
        <div className="tcf-section-title">Task Basics</div>
        <div className="row g-3">
          {/* Task Type/Category failed to load -- retryable, specific
              banner (see fetchCoreOptions above) rather than leaving the
              user staring at a form silently missing its required fields
              with no way to recover short of a full page reload. */}
          {optionsLoadError && (
            <div className="col-md-12">
              <Alert variant="danger" className="d-flex justify-content-between align-items-center mb-0">
                <span>Unable to load complaint categories. Please retry.</span>
                <Button size="sm" variant="outline-danger" onClick={fetchCoreOptions}>
                  Retry
                </Button>
              </Alert>
            </div>
          )}

          {/* Work-ticket flow: choose Project / Module first. */}
          {!isComplaintMode && (
            <>
              <div className="col-md-6">
                <Form.Label className="tcf-label-row">
                  <span>
                    Project{" "}
                    {!!selectedTaskType?.project_required && (
                      <span className="text-danger">*</span>
                    )}
                  </span>
                  <Can moduleKey="projects" actionKey="create">
                    <button
                      type="button"
                      className="tcf-inline-add"
                      disabled={!!selectedTaskType && !selectedTaskType.project_allowed}
                      title={
                        selectedTaskType && !selectedTaskType.project_allowed
                          ? "The selected task type does not allow a Project."
                          : undefined
                      }
                      onClick={() => setShowNewProjectModal(true)}
                    >
                      + New Project
                    </button>
                  </Can>
                </Form.Label>

                <SearchableSelect
                  options={projects.map((p) => ({
                    value: String(p.id),
                    label: p.name,
                  }))}
                  value={form.project_id}
                  onChange={handleProjectChange}
                  isDisabled={!!selectedTaskType && !selectedTaskType.project_allowed}
                  placeholder={
                    selectedTaskType && !selectedTaskType.project_allowed
                      ? "Project not available for this task type"
                      : "Select project..."
                  }
                />
                {projectsLoaded && projects.length === 0 && (
                  <Form.Text className="text-muted">
                    No projects available for your access.
                  </Form.Text>
                )}
              </div>

              <div className="col-md-6">
                <Form.Label className="tcf-label-row">
                  <span>
                    Module{" "}
                    {!!selectedTaskType?.project_module_required && (
                      <span className="text-danger">*</span>
                    )}
                  </span>
                  <Can moduleKey="project_modules" actionKey="create">
                    <button
                      type="button"
                      className="tcf-inline-add"
                      disabled={
                        !form.project_id ||
                        (!!selectedTaskType &&
                          !selectedTaskType.project_module_allowed)
                      }
                      title={
                        !form.project_id
                          ? "Select or create a Project first."
                          : selectedTaskType &&
                              !selectedTaskType.project_module_allowed
                            ? "The selected task type does not allow a Module."
                            : undefined
                      }
                      onClick={() => setShowNewModuleModal(true)}
                    >
                      + New Module
                    </button>
                  </Can>
                </Form.Label>

                <SearchableSelect
                  options={projectModules.map((m) => ({
                    value: String(m.id),
                    label: m.name,
                  }))}
                  value={form.project_module_id}
                  onChange={(v) => handleChange("project_module_id", v)}
                  isDisabled={
                    !form.project_id ||
                    loadingModules ||
                    (!!selectedTaskType &&
                      !selectedTaskType.project_module_allowed)
                  }
                  isLoading={loadingModules}
                  placeholder={
                    !form.project_id
                      ? "Select or create a Project first."
                      : selectedTaskType &&
                          !selectedTaskType.project_module_allowed
                        ? "Module not available for this task type"
                        : loadingModules
                          ? "Loading..."
                          : "Select module..."
                  }
                />
              </div>

              <div className="col-md-6">
                <Form.Label>Phase (optional)</Form.Label>
                <SearchableSelect
                  options={projectPhases.map((p) => ({
                    value: String(p.id),
                    label: p.name,
                  }))}
                  value={form.project_phase_id}
                  onChange={handlePhaseChange}
                  isDisabled={!form.project_id}
                  placeholder={!form.project_id ? "Select a Project first." : "Select phase..."}
                />
              </div>

              <div className="col-md-6">
                <Form.Label>Milestone (optional)</Form.Label>
                <SearchableSelect
                  options={milestoneOptions.map((m) => ({
                    value: String(m.id),
                    label: m.name,
                  }))}
                  value={form.project_milestone_id}
                  onChange={(v) => handleChange("project_milestone_id", v)}
                  isDisabled={!form.project_id}
                  placeholder={!form.project_id ? "Select a Project first." : "Select milestone..."}
                />
              </div>
            </>
          )}

          {/* Task Type / Priority come after Project / Module. */}
          {!isComplaintMode && (
            <div className="col-md-6">
              <Form.Label>Task Type</Form.Label>
              <SearchableSelect
                options={visibleTaskTypes.map((t) => ({
                  value: String(t.id),
                  label: t.name,
                }))}
                value={form.task_type_id}
                onChange={handleTaskTypeChange}
                placeholder="Select task type..."
              />
              {selectedTaskType?.description && (
                <Form.Text muted>{selectedTaskType.description}</Form.Text>
              )}
            </div>
          )}

          <div className={isComplaintMode ? "col-md-12" : "col-md-6"}>
            <Form.Label>Priority</Form.Label>
            <SearchableSelect
              options={PRIORITY_VALUES.map((p) => ({
                value: p,
                label: p,
              }))}
              value={form.priority}
              onChange={(v) => handleChange("priority", v)}
              placeholder={
                selectedTaskType?.default_priority
                  ? `Default (${selectedTaskType.default_priority})`
                  : "Not set"
              }
            />
          </div>

          {/* Staff / Technician: no Title/Description -- the task is
              identified by its Category/Subcategory (stored title and
              description are NULL; see utils/taskDisplay.js). */}
          {!isTitleless && (
            <>
              <div className="col-md-12">
                <Form.Label>
                  {isComplaintMode ? "Complaint Title" : "Task Name"}
                </Form.Label>
                <Form.Control
                  placeholder={isComplaintMode ? "Short summary of your complaint" : "Short summary of the task"}
                  value={form.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                />
              </div>

              <div className="col-md-12">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={embedded ? 2 : 3}
                  value={form.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                />
                <Form.Text className="text-muted">Add enough detail for whoever picks this up to understand the work.</Form.Text>
              </div>
            </>
          )}

          {selectedTaskType && (categoryRequired || form.category_id) && (
            <>
              <div className="col-md-6">
                <Form.Label>
                  Category {categoryRequired && <span className="text-danger">*</span>}
                </Form.Label>
                <SearchableSelect
                  options={categories.map((c) => ({ value: String(c.id), label: c.name }))}
                  value={form.category_id}
                  onChange={handleCategoryChange}
                  placeholder="Select category..."
                />
                {/* Phase 52: New Joiner Asset Request is a DIFFERENT, public
                    pre-joining workflow -- this complaint category stays
                    exactly as-is for existing employees (task spec §35).
                    Matched by category NAME, never a hardcoded id (this
                    codebase avoids hardcoded category ids elsewhere too). */}
                {categories.find((c) => String(c.id) === String(form.category_id))?.name === "Asset Allocation / Return" && (
                  <Form.Text className="text-muted">
                    Requesting assets for a new joiner? Use{" "}
                    <a href="/new-joiner-asset-request" target="_blank" rel="noopener noreferrer">New Joiner Asset Request</a>.
                  </Form.Text>
                )}
              </div>
              <div className="col-md-6">
                <Form.Label>
                  Subcategory {subcategoryRequired && <span className="text-danger">*</span>}
                </Form.Label>
                <SearchableSelect
                  options={subCategories.map((sc) => ({ value: String(sc.id), label: sc.name }))}
                  value={form.sub_category_id}
                  onChange={(v) => handleChange("sub_category_id", v)}
                  isDisabled={!form.category_id || loadingSubCategories}
                  isLoading={loadingSubCategories}
                  placeholder={loadingSubCategories ? "Loading..." : "Select subcategory..."}
                />
              </div>

              {/* Technician is never chosen here -- the backend assigns an
                  eligible technician from the caller's own unit on submit;
                  the success screen then shows who it was. */}
              {isComplaintMode && (
                <div className="col-md-12">
                  <Form.Text className="text-muted" data-testid="complaint-auto-assign-note">
                    Technician will be assigned automatically based on your unit.
                  </Form.Text>
                </div>
              )}
            </>
          )}

          {/* Simplified backdated Task Date (Phase 37, simplified) --
              available to EVERY OTHER role/permission that can already
              reach this form at all; no create_historical permission, no
              feature flag. Defaults to today (seeded by its own effect
              above); `max` blocks a future pick in the UI as a courtesy
              only -- the backend independently re-validates and rejects a
              future date regardless of what a crafted request sends.

              Staff is the one exception: an ordinary employee raising an
              IT complaint has no legitimate reason to backdate it, so the
              field is locked to today and read-only here -- matching the
              backend, which now ignores any task_date Staff sends and
              always uses the real submission moment
              (services/taskCreationService.js#createCommonTask). This
              never touches TAT/due-date calculation either way -- that has
              always started from the actual current moment, never from
              Task Date. */}
          <div className="col-md-6">
            <Form.Label>
              {isStaff ? "Complaint Date" : "Task Date"} <span className="text-danger">*</span>
            </Form.Label>
            {isStaff ? (
              <>
                <Form.Control type="date" value={form.task_date} disabled readOnly />
                <Form.Text className="text-muted">
                  Complaints are recorded with today's date and can't be backdated.
                </Form.Text>
              </>
            ) : (
              <Form.Control
                type="date"
                max={todayDateString()}
                value={form.task_date}
                onChange={(e) => handleChange("task_date", e.target.value)}
              />
            )}
          </div>
        </div>
      </div>

      {!isComplaintMode && (
        <div className="tcf-section">
          <div className="tcf-section-title">Assignment</div>
          <div className="row g-3">
            <div className="col-md-4">
              <Form.Label>Department (optional)</Form.Label>
              <SearchableSelect
                options={departments.map((d) => ({ value: String(d.id), label: d.name }))}
                value={form.department_id}
                onChange={(v) => handleChange("department_id", v)}
                placeholder="Unclassified"
              />
            </div>

            <div className="col-md-4">
              <Form.Label>Assigned To (optional)</Form.Label>
              <ProjectMemberPicker
                users={assigneeOptions}
                value={form.assigned_to}
                onChange={(id) => handleChange("assigned_to", id)}
                placeholder={form.project_id ? "Select an active project member..." : "Select an employee..."}
                isDisabled={!form.project_id && assignableUsers.length === 0}
              />
              {form.project_id && assigneeOptions.length === 0 && (
                <Form.Text className="text-muted">This project has no active members yet.</Form.Text>
              )}
              {!form.project_id && assignableUsers.length === 0 && (
                <Form.Text className="text-muted">You don't have permission to assign this task to anyone yet — it will be created unassigned.</Form.Text>
              )}
            </div>

            <div className="col-md-4">
              <Form.Label>Staff / User (optional — defaults to you)</Form.Label>
              <ProjectMemberPicker
                users={requestForOptions}
                value={form.requested_for}
                onChange={(id) => handleChange("requested_for", id)}
                placeholder="Select who this task is for..."
              />
              <Form.Text className="text-muted">Who this task is being created on behalf of.</Form.Text>
            </div>
          </div>
        </div>
      )}

      {form.task_type_id && (
        <div className={embedded ? "mt-3 p-3 border rounded" : "unit-card mt-3"} style={embedded ? undefined : { padding: 16 }}>
          <div className="fw-semibold mb-1">Estimated TAT</div>
          {loadingTatPreview ? (
            <div className="text-muted small">Calculating...</div>
          ) : tatPreview?.source ? (
            <div className="small">
              <div>
                TAT Source:{" "}
                <strong>{tatPreview.source === "IT_SUBCATEGORY" ? "Subcategory" : tatPreview.source}</strong>
              </div>
              <div>Applied TAT: {tatPreview.tat_value} {tatPreview.tat_unit}</div>
              <div>Estimated due date: {tatPreview.due_at}</div>
            </div>
          ) : subCategoryHasTat ? (
            <div className="small">
              <div>Estimated TAT: {selectedSubCategory.tat_value} {selectedSubCategory.tat_unit}</div>
            </div>
          ) : (
            <div className="text-muted small">
              No Turnaround Time is configured for this subcategory yet — it will be created without a due date.
            </div>
          )}
        </div>
      )}

      <div className={embedded ? "tcm-actions mt-3 d-flex gap-2 justify-content-end" : "mt-4 d-flex gap-2 justify-content-end"}>
        {onCancel && (
          <Button variant="outline-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button className="unit-btn unit-btn-primary" onClick={handleSubmit} disabled={saving || !form.task_type_id}>
          {saving ? "Submitting..." : isComplaintMode ? "Submit Complaint" : isDevOrDA ? "Create Ticket" : "Create Task"}
        </Button>
      </div>

      <QuickCreateProjectModal
        show={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
        onCreated={handleProjectCreated}
      />
      <QuickCreateModuleModal
        show={showNewModuleModal}
        projectId={form.project_id}
        projectName={selectedProject?.name}
        onClose={() => setShowNewModuleModal(false)}
        onCreated={handleModuleCreated}
      />

      <style>{`
        .tcf-section + .tcf-section { margin-top: 22px; }
        .tcf-section-title {
          font-size: 12px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .6px;
          color: var(--admin-primary, var(--primary, #6d1530));
          margin-bottom: 10px;
          padding-bottom: 6px;
          border-bottom: 1px solid #eef2f0;
        }
        .tcf-label-row { display: flex; align-items: center; justify-content: space-between; width: 100%; }
        .tcf-inline-add {
          border: none;
          background: none;
          color: var(--admin-primary, var(--primary, #6d1530));
          font-size: 11px;
          font-weight: 700;
          padding: 0;
          cursor: pointer;
        }
        .tcf-inline-add:hover:not(:disabled) { text-decoration: underline; }
        .tcf-inline-add:disabled { color: #94a3b8; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
